import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ClientTrail, { tabCrumbs } from '../components/ClientTrail';
import { supabase } from '../lib/supabase';
import { downloadSupervisorVisitDoc } from '../lib/generateSupervisorVisitDoc';
import { format, parseISO } from 'date-fns';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMMM d, yyyy'); } catch { return d; }
};
const fmt = (s) => s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—';
const stars = (n) => n ? '★'.repeat(Number(n)) + '☆'.repeat(5 - Number(n)) + `  ${n}/5` : '—';

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase',
      letterSpacing: '.08em', paddingBottom: 8, borderBottom: '2px solid var(--accent)', marginBottom: 14 }}>
      {title}
    </div>
    {children}
  </div>
);

const Row = ({ label, value, full }) => (
  <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '0.5px solid var(--border)',
    flexDirection: full ? 'column' : 'row' }}>
    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', minWidth: full ? 'auto' : 160, flexShrink: 0 }}>{label}</span>
    <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 500, whiteSpace: full ? 'pre-wrap' : 'normal', lineHeight: 1.6 }}>{value || '—'}</span>
  </div>
);

const Check = ({ label, checked }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0' }}>
    <span style={{ fontSize: 15, color: checked ? 'var(--success)' : 'var(--text3)' }}>{checked ? '☑' : '☐'}</span>
    <span style={{ fontSize: 12, color: checked ? 'var(--text)' : 'var(--text3)' }}>{label}</span>
  </div>
);

export default function SupervisorVisitReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('supervisor_visits')
        .select('*, clients(*)')
        .eq('id', id)
        .single();
      if (data) {
        setVisit(data);
        setClient(data.clients || null);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const handleDownload = async () => {
    setDownloading(true);
    try { await downloadSupervisorVisitDoc({ visit, client }); }
    catch (e) { console.error(e); }
    finally { setDownloading(false); }
  };

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div className="loading-spinner" style={{ margin: '0 auto' }} />
    </div>
  );

  if (!visit) return (
    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
      Visit not found.
    </div>
  );

  const clientName = client
    ? `${client.last_name}, ${client.first_name}`
    : visit.client_name || '—';

  return (
    <div style={{ padding: '0 24px 20px', maxWidth: 860, margin: '0 auto' }}>
      {(client?.id || visit.client_id)
        ? <ClientTrail client={client?.id ? client : null} clientId={visit.client_id} crumbs={[...tabCrumbs(client?.id || visit.client_id, 'clinical', 'supervision'), { label: 'Visit report' }]} />
        : <ClientTrail root={[{ label: 'Supervisory Visits', to: '/hh/supervisor-visits' }]} crumbs={[{ label: 'Visit report' }]} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 24 }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer',
            fontSize: 20, padding: '2px 6px', lineHeight: 1, marginTop: 2 }}>←</button>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>
            Supervisory Visit Report
          </h2>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--text3)' }}>
            {clientName} · {visit.client_ref || client?.client_id || ''} · {fmtDate(visit.visit_date)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => navigate(`/hh/supervisor-visits/${id}`)}
            className="btn btn-outline btn-sm">
            Edit Form
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="btn btn-primary btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {downloading ? 'Generating…' : '↓ Download DOCX'}
          </button>
        </div>
      </div>

      {/* Visit summary band */}
      <div style={{ background: 'var(--accent-bg)', border: '1px solid var(--accent-border)',
        borderRadius: 10, padding: '14px 20px', marginBottom: 24,
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        {[
          ['Visit Type',    fmt(visit.visit_type)],
          ['Supervisor',    visit.supervisor_name || '—'],
          ['Assigned Nurse',visit.assigned_nurse_name || '—'],
          ['Duration',      visit.visit_duration_minutes ? `${visit.visit_duration_minutes} min` : '—'],
          ['Nurse Present', visit.nurse_present === true ? 'Yes' : visit.nurse_present === false ? 'No' : '—'],
          ['Next Visit Due',fmtDate(visit.next_visit_due)],
        ].map(([l, val]) => (
          <div key={l}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{l}</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginTop: 3 }}>{val}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* LEFT COLUMN */}
        <div>

          {/* Section 1 — Clinical Observation */}
          <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
            <Section title="1 · Clinical Observation">
              <Row label="Overall Condition"    value={fmt(visit.overall_condition)} />
              <Row label="Vital Signs"          value={visit.vital_signs_status} />
              <Row label="Skin Integrity"       value={fmt(visit.skin_integrity)} />
              <Row label="Equipment"            value={fmt(visit.equipment_functioning)} />
              <Row label="Medication Technique" value={fmt(visit.medication_technique)} />
              <Row label="Med List Current"     value={visit.medication_list_current} />
              {visit.clinical_notes && (
                <Row label="Clinical Notes" value={visit.clinical_notes} full />
              )}
            </Section>
          </div>

          {/* Section 3 — Family Satisfaction */}
          <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
            <Section title="3 · Family Satisfaction">
              <Row label="Overall Satisfaction"    value={stars(visit.satisfaction_overall)} />
              <Row label="Nurse Consistency"       value={stars(visit.satisfaction_consistency)} />
              <Row label="Communication"           value={stars(visit.satisfaction_communication)} />
              <Row label="Care Plan Understanding" value={stars(visit.satisfaction_care_plan)} />
              {visit.family_concerns && (
                <Row label="Family Concerns" value={visit.family_concerns} full />
              )}
            </Section>
          </div>

        </div>

        {/* RIGHT COLUMN */}
        <div>

          {/* Section 2 — Nurse Performance */}
          <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
            <Section title="2 · Nurse Performance">
              <div style={{ marginBottom: 12 }}>
                <Check label="Documentation current and complete"      checked={visit.documentation_current} />
                <Check label="Care plan followed as written"           checked={visit.care_plan_followed} />
                <Check label="Infection control measures observed"     checked={visit.infection_control_observed} />
                <Check label="Emergency protocols reviewed with family" checked={visit.emergency_protocol_reviewed} />
                <Check label="Positive family interaction noted"       checked={visit.family_interaction_ok} />
                <Check label="Caregiver education ongoing"             checked={visit.caregiver_education_ongoing} />
              </div>
              <Row label="Performance Rating" value={fmt(visit.performance_rating)} />
              {visit.strengths && <Row label="Strengths" value={visit.strengths} full />}
              {visit.improvements_needed && <Row label="Improvements Needed" value={visit.improvements_needed} full />}
              {visit.competency_followup?.length > 0 && (
                <Row label="Competency Follow-up"
                  value={Array.isArray(visit.competency_followup) ? visit.competency_followup.join(', ') : visit.competency_followup} />
              )}
              {visit.followup_visit_required && (
                <Row label="Follow-up Visit" value={`Required — ${fmtDate(visit.followup_visit_date)}`} />
              )}
            </Section>
          </div>

          {/* Section 4 — Physician Summary */}
          <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
            <Section title="4 · Physician Summary">
              <Row label="Physician"        value={visit.physician_name} />
              <Row label="Delivery Method"  value={fmt(visit.physician_delivery_method)} />
              <Row label="Fax / Email"      value={visit.physician_fax_email} />
              <Row label="Period"
                value={`${fmtDate(visit.summary_period_start)} → ${fmtDate(visit.summary_period_end)}`} />
              {visit.final_summary && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 6 }}>SUMMARY TEXT</div>
                  <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.7, background: 'var(--bg)',
                    borderRadius: 7, padding: '12px 14px', border: '1px solid var(--border)',
                    whiteSpace: 'pre-wrap' }}>
                    {visit.final_summary.replace(/\*\*/g, '')}
                  </div>
                </div>
              )}
              {visit.recommended_physician_actions && (
                <Row label="Recommended Actions" value={visit.recommended_physician_actions} full />
              )}
            </Section>
          </div>

        </div>
      </div>

      {/* Attestation footer */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
        <Section title="Attestation">
          <p style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.7, margin: '0 0 12px' }}>
            I, <strong>{visit.completed_by || visit.supervisor_name}</strong>, attest that I conducted
            this supervisory visit in person on <strong>{fmtDate(visit.visit_date)}</strong>, that all
            findings documented herein are accurate, and that the physician summary has been reviewed
            and is ready to send.
          </p>
          <div style={{ display: 'flex', gap: 40, marginTop: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontFamily: 'Georgia, serif', fontStyle: 'italic',
                color: 'var(--success)', borderBottom: '1px solid var(--border)', paddingBottom: 4, minWidth: 200 }}>
                {visit.completed_by || visit.supervisor_name || ''}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>Supervisor Signature</div>
            </div>
            <div>
              <div style={{ fontSize: 13, color: 'var(--text)', borderBottom: '1px solid var(--border)',
                paddingBottom: 4, minWidth: 140 }}>
                {fmtDate(visit.visit_date)}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>Date</div>
            </div>
          </div>
        </Section>
      </div>

      {/* Bottom download button */}
      <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 40 }}>
        <button onClick={handleDownload} disabled={downloading}
          className="btn btn-primary"
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 28px', fontSize: 14 }}>
          {downloading ? 'Generating…' : '↓ Download Full DOCX Report'}
        </button>
      </div>

    </div>
  );
}
